import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { ENQUIRY_OPTIONS } from "@/data/packages";

const TEAM_SIZES = ["1 to 4", "5 to 20", "21 to 50", "Over 50"];
const EMPTY = { name: "", email: "", message: "", website: "", package: "", team_size: "", contact_pref: "email", phone: "" };

export const LeadForm = ({ dark = false, source = "", defaultPackage = "" }) => {
  const location = useLocation();
  const fromUrl = new URLSearchParams(location.search).get("need") || "";
  const initialPackage = ENQUIRY_OPTIONS.some((o) => o.value === fromUrl) ? fromUrl : defaultPackage;
  const [form, setForm] = useState({ ...EMPTY, package: initialPackage });

  // Package buttons elsewhere on the page pre-select the matching option.
  useEffect(() => {
    const onPick = (e) => {
      if (ENQUIRY_OPTIONS.some((o) => o.value === e.detail)) setForm((f) => ({ ...f, package: e.detail }));
    };
    window.addEventListener("electra:package", onPick);
    return () => window.removeEventListener("electra:package", onPick);
  }, []);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", { ...form, page: source || location.pathname });
      toast.success("Message sent.", {
        description: "An Electra HR adviser will reply within one working day with a fixed quote.",
      });
      setForm({ ...EMPTY, package: defaultPackage });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Couldn't send that.", {
        description: typeof detail === "string" ? detail : "Please try again, or email hello@electra-hr.com directly.",
      });
    } finally {
      setSending(false);
    }
  };

  const field = `w-full border-2 bg-transparent px-4 py-3 text-sm outline-none transition-colors placeholder:text-current placeholder:opacity-40 focus:border-pink ${
    dark ? "border-white/20 text-paper" : "border-ink/20 text-ink"
  }`;

  return (
    <form data-testid="lead-form" onSubmit={submit} className="space-y-4" noValidate={false}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`lead-name-${dark}`} className="eyebrow mb-2 block opacity-60">Name</label>
          <input
            id={`lead-name-${dark}`}
            data-testid="lead-form-name-input"
            required minLength={2}
            value={form.name}
            onChange={set("name")}
            placeholder="Your name"
            className={field}
          />
        </div>
        <div>
          <label htmlFor={`lead-email-${dark}`} className="eyebrow mb-2 block opacity-60">Work email</label>
          <input
            id={`lead-email-${dark}`}
            data-testid="lead-form-email-input"
            type="email" required
            value={form.email}
            onChange={set("email")}
            placeholder="you@company.co.uk"
            className={field}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`lead-package-${dark}`} className="eyebrow mb-2 block opacity-60">What do you need?</label>
          <select
            id={`lead-package-${dark}`}
            data-testid="lead-form-package-select"
            value={form.package}
            onChange={set("package")}
            className={`${field} ${dark ? "[&>option]:text-ink" : ""}`}
          >
            <option value="">Choose one (optional)</option>
            {ENQUIRY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={`lead-team-${dark}`} className="eyebrow mb-2 block opacity-60">Team size</label>
          <select
            id={`lead-team-${dark}`}
            data-testid="lead-form-team-select"
            value={form.team_size}
            onChange={set("team_size")}
            className={`${field} ${dark ? "[&>option]:text-ink" : ""}`}
          >
            <option value="">Choose (optional)</option>
            {TEAM_SIZES.map((t) => (
              <option key={t} value={t}>{t} people</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor={`lead-message-${dark}`} className="eyebrow mb-2 block opacity-60">What's going on?</label>
        <textarea
          id={`lead-message-${dark}`}
          data-testid="lead-form-message-input"
          required minLength={10} rows={5}
          value={form.message}
          onChange={set("message")}
          placeholder="A line or two about the situation, we'll take it from there."
          className={`${field} resize-y`}
        />
      </div>
      <fieldset>
        <legend className="eyebrow mb-2 block opacity-60">How should we reply?</legend>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <label className="flex items-center gap-2">
            <input type="radio" name={`lead-pref-${dark}`} value="email" checked={form.contact_pref === "email"} onChange={set("contact_pref")} data-testid="lead-form-pref-email" className="accent-pink" />
            By email with a fixed quote
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name={`lead-pref-${dark}`} value="call" checked={form.contact_pref === "call"} onChange={set("contact_pref")} data-testid="lead-form-pref-call" className="accent-pink" />
            A phone or video call with an adviser
          </label>
        </div>
      </fieldset>
      {form.contact_pref === "call" && (
        <div>
          <label htmlFor={`lead-phone-${dark}`} className="eyebrow mb-2 block opacity-60">Phone number</label>
          <input
            id={`lead-phone-${dark}`}
            data-testid="lead-form-phone-input"
            type="tel" required minLength={7} maxLength={30}
            value={form.phone}
            onChange={set("phone")}
            placeholder="So your adviser can ring you"
            className={field}
          />
        </div>
      )}
      {/* Honeypot: hidden from humans, bots fill it */}
      <input
        type="text" name="website" value={form.website} onChange={set("website")}
        tabIndex={-1} autoComplete="off" aria-hidden="true"
        className="absolute left-[-9999px] h-0 w-0 opacity-0"
      />
      <button
        data-testid="lead-form-submit-button"
        type="submit" disabled={sending}
        className="group flex w-full sm:w-auto items-center justify-center gap-2 bg-pink px-8 py-4 font-bold text-ink transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
      >
        {sending ? <Loader2 size={18} className="animate-spin" /> : (
          <>Send it <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" /></>
        )}
      </button>
      <p className={`text-xs ${dark ? "text-paper/40" : "text-ink/40"}`}>
        An HR adviser replies within one working day with a fixed quote. Your details go to a human, never a list.
      </p>
    </form>
  );
};
